import { getFormattedNumber } from '@src/slide/domain/ui-model/element-modeles/property/chart/util/format';
import map from 'lodash/map';
import groupBy from 'lodash/groupBy';
import sum from 'lodash/sum';
import flatten from 'lodash/flatten';
import isNil from 'lodash/isNil';
import { BaseChartProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/base-property';
import { getFirstThemeLineStyle, getThemeColorValueByIndex, getThemeStyleTransformOpt } from '@src/slide/domain/ui-model/element-modeles/property/chart/theme';
import { MarkerType } from '@src/slide/domain/ui-model/element-modeles/property/chart/legend/legend.interface';
import { TrendlineProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/geometry/trendline';
import { transaction } from '@src/slide/domain/slide-app';
import forEach from 'lodash/forEach';
import { DataLabelProperty } from './data-label';
import { SeriesProperty } from './series';
import type { Nullable } from '@src/slide/domain/ui-model/element-modeles/property/chart/chart-shape-property.interface';
import type { DataListItem } from '@src/slide/domain/ui-model/element-modeles/chart-models/sub-chart/series';
import type { IBorderInfo } from '@src/slide/domain/ui-model/element-modeles/property/border/border.interface';
import type { IBackgroundFillInfo } from '@src/slide/domain/ui-model/element-modeles/property/fill/fill.interface';
import type {
    IDataLabel,
    IChartSeries,
    ITrendline, IDataLabels, DataPoint,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart/sub-chart-properties.interface';
import type {
    ITrendlineProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/geometry/trendline/trendline.interface';
import type {
    ILegendItem,
    ILegendMarker,
    LegendContentType,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/legend/legend.interface';
import { SubChartType, STBarGrouping, STGrouping } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sdk-model.interface';
import type { IAxisProperty } from '@src/slide/domain/ui-model/element-modeles/property/chart/axis/axis.interface';
import type {
    IBaseSubChartProperty,
} from '@src/slide/domain/ui-model/element-modeles/property/chart/sub-chart/base-sub-chart.interface';
import type { ISubChartModel } from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/sub-chart';
import type {
    IChartShapeProps,
} from '@src/slide/domain/ui-model/element-modeles/Interface/chart-v3/chart-properties.interface';
import type { ISeriesProperty } from './series/series.interface';
import { IDataItemProperty } from './data-item/data-item.interface';
import { DataItemProperty } from './data-item';
import { IChartModel } from '../../../Interface/chart-v3/chart-model.interface';
import { TDColor } from '../../color/td-color';
import pick from 'lodash/pick';
import isEqual from 'lodash/isEqual';
import sortBy from 'lodash/sortBy';
import get from 'lodash/get';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IEditorAppServices } from '@src/slide/application/entry/toc/editor/base/base-editor-app-service.interface';
import { CANVAS_PT_SCALE } from '@src/slide/infrastructure/module/independent/unit';
import { IThemeModel } from '@src/slide/domain/ui-model/element-modeles/Interface/theme-model.interface';


export class BaseSubChartProperty<SubChart extends ISubChartModel = ISubChartModel> extends
    BaseChartProperty<SubChart> implements IBaseSubChartProperty {
    public type: SubChartType;
    public id: string;
    public data: IDataItemProperty[];
    public xAxis?: IAxisProperty;
    public yAxis?: IAxisProperty;
    public trendlines: ITrendlineProperty[];
    public series: ISeriesProperty[];

    public get relativeAxes(): IAxisProperty[] | undefined {
        return (this as unknown as IBaseSubChartProperty)
            .axisIds?.map(id => this.context.getPropertyModule('axes')?.getRelativeAxisById(id)).filter(v => !!v) as IAxisProperty[];
    }

    public get totalSeriesCount(): number {
        return this.context.getChartModel().plotArea.charts
            .reduce((count, subChart) => count + (subChart.series?.length || 0), 0);
    }

    private get chartModel(): IChartModel {
        return this.context.getChartModel();
    }

    public init() {
        this.type = this.uiData.type;
        this.id = this.uiData.id;

        /* 初始化系列数据 */
        this.initSeries();
        /* 初始化数据源数组，根据系列中数据打平聚合 */
        this.initFlattenData();
        /* 对数据源进行二次处理 */
        this.processData();
        /* 初始化横、纵坐标轴，计算相关属性 */
        this.initAxis();
        /* 初始化趋势线，根据系列中数据进行聚合 */
        this.initTrendline();

        return this;
    }

    public getAxisValuesFromData(axis: IAxisProperty) {
        const subChartModel = this.uiData;
        let values = this.data.map(data => data.value)
            .filter(i => !isNaN(i));
        if (this.isStackedGrouping((subChartModel as any).grouping)) {
            // 堆积时，除了计算堆积值，还要考虑原始值，原始值可能为负数
            const stackedValues = map(groupBy(this.data, 'category'), items => this.formatNumber(sum(items.map(i => +i.value))));
            values = [...values, ...stackedValues];
        }
        return {
            values,
        };
    }

    public getLegendContentType(): LegendContentType {
        let legendContentType: LegendContentType = 'series';
        const { series } = this;

        if (series?.length > 0) {
            const firstCategory = series?.[0].dataList?.[0]?.categoryPoint?.value;

            // 如果: ser大于1 或者 分类数据是日期， 则按ser划分
            if (series.length > 1 || (firstCategory && !isNaN(Date.parse(firstCategory as string)))) {
                legendContentType = 'series';
            }

            // series等1的情况下：有dataPoint数据 或者 依数据点作色，使用category作为图例内容
            // 注意，这个优先级更高，所以写在下面，会覆盖上面的判定

            if (this.totalSeriesCount === 1) {
                const isVaryColors = !!(this as any).varyColors;
                const hasDataPoint = this.series?.[0]?.dataList?.some((dataListItem) => {
                    const { dataPoint } = dataListItem;
                    return !!dataPoint;
                });

                if (isVaryColors || hasDataPoint) {
                    legendContentType = 'category';
                }
            }
        }

        return legendContentType;
    }

    public getLegendMarker(props: IChartShapeProps): ILegendMarker {
        return {
            symbol: MarkerType.Rect,
            props,
        };
    }

    public processLegendItems(items: ILegendItem[]): ILegendItem[] {
        return items;
    }

    public processAxis(axis: IAxisProperty) {}

    // 图表是否是堆叠，用于坐标轴数据处理
    public isStackedGrouping(grouping: STGrouping | STBarGrouping) {
        return grouping === STGrouping.stacked;
    }

    public isPercentStackedGrouping(grouping: STGrouping | STBarGrouping): boolean {
        return grouping === STGrouping.percentStacked;
    }

    public removeTrendlines() {
        transaction(() => {
            forEach(this.series, ser => ser.removeTrendlines());
        });
    }

    public removeDataLabels() {
        transaction(() => {
            // 删除 series 下 datalabels
            forEach(this.series, ser => ser.removeDataLabels());
        });
    }
    /**
     * 更新 subChart下的 datalabels， 如果没有则创建
     * @param props
     */
    public updateDataLabels(props?: Partial<Nullable<IDataLabels>>) {
        transaction(() => {
            forEach(this.series, ser => ser.updateDataLabels(props));
        });
    }

    public updateProps(updateProps: Partial<ISubChartModel>, removeProps: Partial<ISubChartModel>) {
        const subChartModel = this.uiData;
        subChartModel.updateAndRemoveProps(
            updateProps,
            removeProps,
        );
    }

    /**
     * 获取seriesProperty
     */
    public getSeries(seriesId) {
        return this.series.find(ser => ser.seriesId === seriesId);
    }

    /**
     * 获取dataPointProperty
     * @param dataId
     * @returns
     */
    public getDataItem(dataId): IDataItemProperty | undefined {
        return this.data.find(data => data.id === dataId);
    }

    public getDataLabelProperty(dataLabel: IDataLabel, dataItem: IDataItemProperty) {
        return new DataLabelProperty(dataLabel, this.context).init(dataItem);
    }

    public getTrendline(seriesId, trendlineIndex) {
        const trendline = this.trendlines.find(item => item.seriesId === seriesId && item.idx === trendlineIndex);
        return trendline;
    }

    /**
     * 切换主题
     * @param chartColor
     * @returns
     */
    public updateChartColorsWithTheme(chartColor) {
        const subChartModel = this.uiData;
        const updateSerProps = map(this.series, (ser) => {
            const serItem: any = {
                props: {
                    style: this.getDefaultThemeStyle(ser, ser.idx!, chartColor.id, this.totalSeriesCount),
                },
            };
            if (ser.marker?.props) {
                serItem.marker = {
                    props: {
                        style: this.getDefaultMarkerThemeStyle(ser.idx!, chartColor.id, this.totalSeriesCount),
                    },
                };
            }
            const dataItemNum = this.series[0].dataList.length;
            if (subChartModel.varyColors) {
                serItem.dataList = ser.dataList.map((dataItem, index) => {
                    const dataPoint: any = {
                        props: {
                            style: this.getDefaultThemeStyle(dataItem.dataPoint!, index, chartColor.id, dataItemNum),
                        },
                    };
                    if (dataItem.dataPoint?.marker?.props) {
                        dataPoint.marker = {
                            props: {
                                style: this.getDefaultMarkerThemeStyle(index, chartColor.id, this.totalSeriesCount),
                            },
                        };
                    }
                    return { dataPoint };
                });
            }
            if (ser.otherDPt) {
                serItem.otherDPt = {
                    props: {
                        style: this.getDefaultThemeStyle(ser.otherDPt, ser.dataList.length, chartColor.id, dataItemNum),
                    },
                };
            }
            return serItem;
        });
        const removeSerProps = subChartModel.varyColors ? undefined : map(this.series, (ser) => {
            const serItem = {
                dataList: ser.dataList.map(dataItem => ({
                    dataPoint: null,
                })),
            };
            return serItem;
        });
        subChartModel.updateAndRemoveProps({ series: updateSerProps }, { series: removeSerProps });
    }

    public isAutoThemeColor(target: IChartSeries | DataPoint, index, total = this.totalSeriesCount) {
        if (!get(target.props?.style, this.getGeometryColorPath())?.isSchemeColor) return false;
        const themeStyle = this.getDefaultThemeStyle(target, index!, this.chartModel.chartSpace?.colorStyle?.id, total);
        const colorKey = ['colorValue', 'lumMod', 'lumOff', 'shade', 'tint'];

        return isEqual(
            pick(get(target.props?.style, this.getGeometryColorPath()), colorKey),
            pick(get(themeStyle, this.getGeometryColorPath()), colorKey),
        );
    }

    public getGeometryColorPath = () => 'fillBackground.solidFill.color';

    public getDefaultThemeStyle = (target: IChartSeries | DataPoint, index: number, colorPaletteId?: string, total?: number): IChartShapeProps['style'] => {
        const color = this.getThemeColor(index, colorPaletteId, total);
        return {
            fillBackground: {
                solidFill: {
                    color,
                },
            },
            border: {
                noFill: 1,
            },
        };
    };

    public getDefaultMarkerThemeStyle = (index: number, colorPaletteId?: string, total?: number): IChartShapeProps['style'] | null => {
        const { style } = this.context.getChartModel();
        const { borderScale, accentStartIndex } = getThemeStyleTransformOpt(style);
        const color = this.getThemeColor(index + accentStartIndex, colorPaletteId, total);
        const themeLineStyle = getFirstThemeLineStyle(this.getThemeColorManager()?.currentThemeModel as IThemeModel);
        return {
            fillBackground: {
                solidFill: {
                    color,
                },
            },
            border: {
                ...themeLineStyle,
                width: (themeLineStyle?.width || 0) * borderScale || 1.5 * CANVAS_PT_SCALE,
                color,
                tdColor: color,
            },
        };
    };

    public getChartOriginType(): SubChartType {
        return this.context.getSubChartModel().originType;
    }

    protected getThemeColor = (index: number, colorPaletteId?: string, total?: number) => {
        const paletteId = colorPaletteId || this.chartModel.chartSpace?.colorStyle?.id as string;
        const totalCount = total || this.totalSeriesCount;
        const color = getThemeColorValueByIndex(index, paletteId, totalCount) as TDColor;
        return color;
    };

    protected initAxis() {
        const subChart = this.context.getSubChartModel();
        if (!subChart?.axisIds) return;

        const axesProperty = this.context.getPropertyModule('axes');
        if (!axesProperty) return;

        // 默认第一个为X轴， 第二个为Y轴
        const [xAxisId, yAxisId] = subChart.axisIds;
        this.xAxis = axesProperty.getRelativeAxisById(xAxisId);
        this.yAxis = axesProperty.getRelativeAxisById(yAxisId);

        // 注意：由于坐标轴的数据，依赖所有子表的数据（共用坐标轴的情况需要聚合所有的数据进行计算），所以会在所有子表property初始化完之后，进行values的设置
        // values会在上层的设置propertyModules进行调用，见PropertyModules.init()
    }

    protected initSeries() {
        const { series } = this.uiData;
        this.series = [];
        series?.forEach((ser, index) => {
            const serProperty = new SeriesProperty(ser, this.context).init(index);
            this.series.push(serProperty);
        });
    }

    protected initFlattenData() {
        const { series, type } = this;
        const { dateAxes } = this.context.getChartModel().plotArea;
        // 如果坐标轴内categoryPoint有非数字string，则判定为非日期坐标轴
        // 理论上应该不会有这种情况， 但现在数据编辑时添加string类别后会出现这样的情况，后续修复
        const hasDateAxis = !!dateAxes?.length
            && series[0].dataList.every(data => !isNaN(Number(data.categoryPoint?.value)));

        this.data = flatten(series.map((serItem, serIndex) => serItem.dataList!
            // 当值坐标轴为日期坐标轴时，过滤掉categoryPoint为空值的点
            .filter(dataItem => !(hasDateAxis && isNil(dataItem.categoryPoint?.value)))
            .map((dataItem, index): IDataItemProperty => {
                // @ts-ignore
                const item = this.initDataItem({ dataItem, index, serIndex, serItem: serItem.uiData });
                return item;
            })));

        if (hasDateAxis) {
            this.data = sortBy(this.data, 'category');
        }
    }

    protected initDataItem(param: {
        dataItem: DataListItem,
        index: number,
        serItem: IChartSeries,
        serIndex: number,
    }): IDataItemProperty {
        return new DataItemProperty(param.dataItem, this.context).init(param);
    }

    protected initTrendline() {
        this.trendlines = [];
        this.series.forEach((ser) => {
            ser.trendlines?.forEach((trendline) => {
                this.trendlines.push(this.getTrendlineProperty(trendline, ser.getUIData()));
            });
        });
    }

    protected getTrendlineProperty(trendline: ITrendline, ser: IChartSeries) {
        return new TrendlineProperty(trendline, this.context).init(this.data, ser);
    }

    /**
     * 进行进一步的预处理，做一些数据清洗、根据不同图表类型做一些不同的处理，如散点图要把x也转成number
     */
    protected processData() {
        this.data = this.data.map(item => Object.assign(item, {
            // 横坐标轴上，可能存在用户手动删除数据导致的空数据点，但是count依赖是删除前的，图表中会保留占位，但是是空数据
            // 这里用不同长度的空字符串来占位，保证他们不会被识别为一个category而被叠加到一起
            category: item.category ?? new Array(item.categoryIndex).fill(' ')
                .join(''),
        }));
    }

    protected createBorderFillWithFillBackground(fillBackground?: IBackgroundFillInfo) {
        const borderProps: IBorderInfo = {};
        if (fillBackground?.solidFill) {
            borderProps.color = fillBackground?.solidFill.color.srgb_color;
        } else if (fillBackground?.gradFill) {
            borderProps.gradFill = fillBackground.gradFill;
        } else if (fillBackground?.noFill) {
            borderProps.noFill = fillBackground.noFill;
        } else {
            borderProps.color = undefined;
        }

        return borderProps;
    }

    protected getThemeColorManager() {
        return (AppServiceProvider.getInstance().getAppServices() as IEditorAppServices)
            .editableServiceLayer?.themeColorManager;
    }

    private formatNumber(n) {
        if (isNaN(n) || isNil(n)) return n;
        return getFormattedNumber(n);
    }
}


